/**
 * ProfileDetector - Detects closed regions (profiles) in CAD sketches
 *
 * Analyzes sketch entities to find closed loops and classifies them as:
 * - Outer boundaries (CCW orientation)
 * - Inner holes (CW orientation)
 *
 * Algorithm:
 * 1. Build connectivity graph from sketch entities
 * 2. Find all closed loops using face-finding algorithm
 * 3. Determine loop orientation (CW/CCW using shoelace formula)
 * 4. Build profile hierarchy (outer loops with nested holes)
 * 5. Use even-odd rule for complex nesting
 */

class ProfileDetector {
  constructor() {
    this.tolerance = 1e-6; // Geometric tolerance for coincidence
  }

  /**
   * Detect all profiles in a sketch
   * @param {Array} entities - Array of sketch entities (lines, arcs, circles, etc.)
   * @returns {Array<Profile>} - Array of detected profiles
   */
  detectProfiles(entities) {
    if (!entities || entities.length === 0) {
      return [];
    }

    console.log('[ProfileDetector] Detecting profiles from', entities.length, 'entities');

    // Step 1: Build edge graph
    const graph = this.buildEdgeGraph(entities);

    if (!graph || graph.edges.length === 0) {
      console.warn('[ProfileDetector] No valid edges in graph');
      return [];
    }

    // Step 2: Find all minimal cycles (closed loops)
    const loops = this.findClosedLoops(graph);

    if (loops.length === 0) {
      console.warn('[ProfileDetector] No closed loops found');
      return [];
    }

    console.log('[ProfileDetector] Found', loops.length, 'closed loops');

    // Step 3: Determine loop orientation and compute properties
    const orientedLoops = loops.map(loop => {
      const points = this.getLoopPoints(loop, graph);
      const area = this.computeSignedArea(points);
      const centroid = this.computeCentroid(points);

      return {
        edges: loop,
        vertices: this.getLoopVertices(loop, graph),
        clockwise: area < 0, // Negative area = clockwise
        area: Math.abs(area),
        centroid: centroid,
        points: points
      };
    });

    // Step 4: Build profile hierarchy (outer vs inner loops)
    const profiles = this.buildProfileHierarchy(orientedLoops);

    console.log('[ProfileDetector] Detected', profiles.length, 'profiles');

    return profiles;
  }

  /**
   * Build connectivity graph from sketch entities
   */
  buildEdgeGraph(entities) {
    const graph = {
      edges: [],
      vertices: new Map(), // vertex key -> {point, edges}
      entityMap: new Map() // edge id -> entity
    };

    // Process each entity
    entities.forEach((entity, index) => {
      if (entity.construction) return; // Skip construction geometry

      const edgeId = `edge_${index}`;

      switch (entity.type) {
        case 'line':
          this.addLineToGraph(entity, edgeId, graph);
          break;

        case 'arc':
          this.addArcToGraph(entity, edgeId, graph);
          break;

        case 'circle':
          this.addCircleToGraph(entity, edgeId, graph);
          break;

        case 'rectangle':
          this.addRectangleToGraph(entity, edgeId, graph);
          break;

        case 'polygon':
          this.addPolygonToGraph(entity, edgeId, graph);
          break;
      }
    });

    return graph;
  }

  /**
   * Add line entity to graph
   */
  addLineToGraph(entity, edgeId, graph) {
    const startKey = this.vertexKey(entity.start);
    const endKey = this.vertexKey(entity.end);

    const edge = {
      id: edgeId,
      type: 'line',
      start: entity.start,
      end: entity.end,
      startKey: startKey,
      endKey: endKey,
      entity: entity
    };

    graph.edges.push(edge);
    graph.entityMap.set(edgeId, entity);

    // Add vertices
    this.addVertex(graph, startKey, entity.start, edge);
    this.addVertex(graph, endKey, entity.end, edge);
  }

  /**
   * Add arc entity to graph
   */
  addArcToGraph(entity, edgeId, graph) {
    const startKey = this.vertexKey(entity.start);
    const endKey = this.vertexKey(entity.end);

    const edge = {
      id: edgeId,
      type: 'arc',
      start: entity.start,
      end: entity.end,
      center: entity.center,
      radius: entity.radius,
      clockwise: entity.clockwise,
      startKey: startKey,
      endKey: endKey,
      entity: entity
    };

    graph.edges.push(edge);
    graph.entityMap.set(edgeId, entity);

    this.addVertex(graph, startKey, entity.start, edge);
    this.addVertex(graph, endKey, entity.end, edge);
  }

  /**
   * Add circle entity to graph (self-loop)
   */
  addCircleToGraph(entity, edgeId, graph) {
    // Circle is a special case - it's a closed loop by itself
    const centerKey = this.vertexKey(entity.center);

    const edge = {
      id: edgeId,
      type: 'circle',
      center: entity.center,
      radius: entity.radius,
      startKey: centerKey,
      endKey: centerKey,
      closed: true,
      entity: entity
    };

    graph.edges.push(edge);
    graph.entityMap.set(edgeId, entity);
  }

  /**
   * Add rectangle entity to graph (decompose into 4 lines)
   */
  addRectangleToGraph(entity, edgeId, graph) {
    const { start, end } = entity;

    // Rectangle corners
    const corners = [
      { x: start.x, y: start.y },
      { x: end.x, y: start.y },
      { x: end.x, y: end.y },
      { x: start.x, y: end.y }
    ];

    // Create 4 edges
    for (let i = 0; i < 4; i++) {
      const nextI = (i + 1) % 4;
      const subEdgeId = `${edgeId}_${i}`;

      const lineEntity = {
        type: 'line',
        start: corners[i],
        end: corners[nextI]
      };

      this.addLineToGraph(lineEntity, subEdgeId, graph);
    }
  }

  /**
   * Add polygon entity to graph (decompose into lines)
   */
  addPolygonToGraph(entity, edgeId, graph) {
    if (!entity.points || entity.points.length < 3) return;

    const points = entity.points;

    for (let i = 0; i < points.length; i++) {
      const nextI = (i + 1) % points.length;
      const subEdgeId = `${edgeId}_${i}`;

      const lineEntity = {
        type: 'line',
        start: points[i],
        end: points[nextI]
      };

      this.addLineToGraph(lineEntity, subEdgeId, graph);
    }
  }

  /**
   * Add vertex to graph
   */
  addVertex(graph, key, point, edge) {
    if (!graph.vertices.has(key)) {
      graph.vertices.set(key, {
        point: point,
        edges: []
      });
    }

    graph.vertices.get(key).edges.push(edge);
  }

  /**
   * Generate vertex key from point (with tolerance)
   */
  vertexKey(point) {
    const x = Math.round(point.x / this.tolerance) * this.tolerance;
    const y = Math.round(point.y / this.tolerance) * this.tolerance;
    return `${x.toFixed(6)},${y.toFixed(6)}`;
  }

  /**
   * Find all closed loops in the graph
   */
  findClosedLoops(graph) {
    const loops = [];
    const visitedFaces = new Set();

    // First, handle circles (self-contained loops)
    graph.edges.forEach(edge => {
      if (edge.type === 'circle' && edge.closed) {
        loops.push([edge]);
      }
    });

    // Find loops using face-finding algorithm
    graph.edges.forEach(edge => {
      if (edge.type === 'circle') return; // Already handled

      // Try both directions
      [true, false].forEach(forward => {
        const faceKey = `${edge.id}_${forward}`;
        if (visitedFaces.has(faceKey)) return;

        const loop = this.traceFace(edge, forward, graph, visitedFaces);

        if (loop && loop.length >= 3) {
          // Verify loop is actually closed
          if (this.isLoopClosed(loop, graph)) {
            loops.push(loop);
          }
        }
      });
    });

    return loops;
  }

  /**
   * Trace a face (closed loop) starting from an edge
   */
  traceFace(startEdge, forward, graph, visitedFaces) {
    const loop = [];
    let currentEdge = startEdge;
    let currentForward = forward;
    let currentVertex = forward ? currentEdge.endKey : currentEdge.startKey;

    const maxIterations = graph.edges.length * 2;
    let iterations = 0;

    do {
      const faceKey = `${currentEdge.id}_${currentForward}`;

      if (visitedFaces.has(faceKey)) {
        // Already visited this face direction
        return null;
      }

      visitedFaces.add(faceKey);
      loop.push({ edge: currentEdge, forward: currentForward });

      // Get vertex we're at
      const vertex = graph.vertices.get(currentVertex);
      if (!vertex || vertex.edges.length === 0) {
        return null; // Dead end
      }

      // Find next edge (leftmost turn)
      const nextInfo = this.findNextEdge(currentEdge, currentForward, vertex, graph);

      if (!nextInfo) {
        return null; // No valid next edge
      }

      currentEdge = nextInfo.edge;
      currentForward = nextInfo.forward;
      currentVertex = currentForward ? currentEdge.endKey : currentEdge.startKey;

      iterations++;
      if (iterations > maxIterations) {
        console.warn('[ProfileDetector] Max iterations reached in face tracing');
        return null;
      }

    } while (currentEdge !== startEdge || currentForward !== forward);

    return loop.length >= 3 ? loop : null;
  }

  /**
   * Find the next edge in the loop (leftmost turn)
   */
  findNextEdge(currentEdge, currentForward, vertex, graph) {
    const currentPoint = currentForward ? currentEdge.end : currentEdge.start;
    const prevPoint = currentForward ? currentEdge.start : currentEdge.end;

    // Direction vector of current edge (pointing to current vertex)
    const incomingDir = {
      x: currentPoint.x - prevPoint.x,
      y: currentPoint.y - prevPoint.y
    };

    let bestEdge = null;
    let bestAngle = -Infinity;
    let bestForward = true;

    // Check all edges at this vertex
    vertex.edges.forEach(edge => {
      // Try both directions of the edge
      [true, false].forEach(forward => {
        // Skip the edge we came from in the same direction
        if (edge === currentEdge && forward === currentForward) return;

        const edgeStart = forward ? edge.start : edge.end;
        const edgeEnd = forward ? edge.end : edge.start;

        // Edge must start at current vertex
        const startKey = this.vertexKey(edgeStart);
        if (startKey !== this.vertexKey(currentPoint)) return;

        // Direction vector of candidate edge
        const outgoingDir = {
          x: edgeEnd.x - edgeStart.x,
          y: edgeEnd.y - edgeStart.y
        };

        // Compute angle (using atan2 for signed angle)
        const angle = this.computeLeftTurnAngle(incomingDir, outgoingDir);

        if (angle > bestAngle) {
          bestAngle = angle;
          bestEdge = edge;
          bestForward = forward;
        }
      });
    });

    if (!bestEdge) return null;

    return { edge: bestEdge, forward: bestForward };
  }

  /**
   * Compute left turn angle between two direction vectors
   */
  computeLeftTurnAngle(incoming, outgoing) {
    // Normalize vectors
    const inLen = Math.sqrt(incoming.x * incoming.x + incoming.y * incoming.y);
    const outLen = Math.sqrt(outgoing.x * outgoing.x + outgoing.y * outgoing.y);

    if (inLen < this.tolerance || outLen < this.tolerance) return 0;

    const inNorm = { x: incoming.x / inLen, y: incoming.y / inLen };
    const outNorm = { x: outgoing.x / outLen, y: outgoing.y / outLen };

    // Compute signed angle using atan2
    const inAngle = Math.atan2(inNorm.y, inNorm.x);
    const outAngle = Math.atan2(outNorm.y, outNorm.x);

    let angle = outAngle - inAngle;

    // Normalize to [0, 2π)
    while (angle < 0) angle += 2 * Math.PI;
    while (angle >= 2 * Math.PI) angle -= 2 * Math.PI;

    return angle;
  }

  /**
   * Check if a loop is actually closed
   */
  isLoopClosed(loop, graph) {
    if (loop.length === 0) return false;

    // For circle, it's always closed
    if (loop.length === 1 && loop[0].edge.type === 'circle') {
      return true;
    }

    const firstEdge = loop[0];
    const lastEdge = loop[loop.length - 1];

    const startKey = firstEdge.forward ? firstEdge.edge.startKey : firstEdge.edge.endKey;
    const endKey = lastEdge.forward ? lastEdge.edge.endKey : lastEdge.edge.startKey;

    return startKey === endKey;
  }

  /**
   * Get ordered points of a loop
   */
  getLoopPoints(loop, graph) {
    const points = [];

    loop.forEach(({ edge, forward }) => {
      if (edge.type === 'circle') {
        // Sample circle points
        const numSamples = 32;
        for (let i = 0; i < numSamples; i++) {
          const angle = (2 * Math.PI * i) / numSamples;
          points.push({
            x: edge.center.x + edge.radius * Math.cos(angle),
            y: edge.center.y + edge.radius * Math.sin(angle)
          });
        }
      } else if (edge.type === 'arc') {
        // Sample arc points
        const startAngle = Math.atan2(
          edge.start.y - edge.center.y,
          edge.start.x - edge.center.x
        );
        const endAngle = Math.atan2(
          edge.end.y - edge.center.y,
          edge.end.x - edge.center.x
        );

        const numSamples = 16;
        for (let i = 0; i < numSamples; i++) {
          const t = i / numSamples;
          let angle;

          if (edge.clockwise) {
            angle = startAngle - t * (startAngle - endAngle);
          } else {
            angle = startAngle + t * (endAngle - startAngle);
          }

          points.push({
            x: edge.center.x + edge.radius * Math.cos(angle),
            y: edge.center.y + edge.radius * Math.sin(angle)
          });
        }
      } else {
        // Line segment
        const start = forward ? edge.start : edge.end;
        if (points.length === 0 ||
            this.distance(points[points.length - 1], start) > this.tolerance) {
          points.push({ x: start.x, y: start.y });
        }
      }
    });

    return points;
  }

  /**
   * Get ordered vertices of a loop
   */
  getLoopVertices(loop, graph) {
    const vertices = [];

    loop.forEach(({ edge, forward }) => {
      const start = forward ? edge.start : edge.end;
      vertices.push(this.vertexKey(start));
    });

    return vertices;
  }

  /**
   * Compute signed area of a polygon using shoelace formula
   * Positive = CCW, Negative = CW
   */
  computeSignedArea(points) {
    if (points.length < 3) return 0;

    let sum = 0;
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      sum += (p2.x - p1.x) * (p2.y + p1.y);
    }

    return sum / 2;
  }

  /**
   * Compute centroid of a polygon
   */
  computeCentroid(points) {
    if (points.length === 0) return { x: 0, y: 0 };

    let cx = 0, cy = 0;
    points.forEach(p => {
      cx += p.x;
      cy += p.y;
    });

    return {
      x: cx / points.length,
      y: cy / points.length
    };
  }

  /**
   * Build profile hierarchy (outer loops with holes)
   */
  buildProfileHierarchy(loops) {
    if (loops.length === 0) return [];

    // Sort loops by area (largest first)
    loops.sort((a, b) => b.area - a.area);

    const profiles = [];
    const usedLoops = new Set();

    // Process each loop
    loops.forEach((outerLoop, i) => {
      if (usedLoops.has(i)) return;

      // Counter-clockwise loops are outer boundaries
      // Clockwise loops are holes

      const profile = {
        id: `profile_${profiles.length}`,
        outerLoop: null,
        innerLoops: [],
        area: outerLoop.area,
        centroid: outerLoop.centroid
      };

      if (!outerLoop.clockwise) {
        // This is an outer boundary
        profile.outerLoop = outerLoop;
        usedLoops.add(i);

        // Find holes inside this profile
        loops.forEach((innerLoop, j) => {
          if (i === j || usedLoops.has(j)) return;
          if (!innerLoop.clockwise) return; // Only holes are CW

          // Check if this loop is inside the outer loop
          if (this.isPointInPolygon(innerLoop.centroid, outerLoop.points)) {
            profile.innerLoops.push(innerLoop);
            usedLoops.add(j);
          }
        });

        profiles.push(profile);
      } else {
        // This is a hole without an outer boundary (shouldn't happen in valid sketches)
        // Treat it as an outer boundary anyway
        profile.outerLoop = outerLoop;
        usedLoops.add(i);
        profiles.push(profile);
      }
    });

    return profiles;
  }

  /**
   * Check if a point is inside a polygon (even-odd rule)
   */
  isPointInPolygon(point, polygon) {
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;

      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Distance between two points
   */
  distance(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Convert profile to Three.js Shape (for extrusion)
   */
  profileToThreeShape(profile, graph) {
    if (!profile.outerLoop) return null;

    const shape = new THREE.Shape();

    // Build outer boundary
    this.buildShapeFromLoop(shape, profile.outerLoop, graph, true);

    // Add holes
    profile.innerLoops.forEach(holeLoop => {
      const holePath = new THREE.Path();
      this.buildShapeFromLoop(holePath, holeLoop, graph, false);
      shape.holes.push(holePath);
    });

    return shape;
  }

  /**
   * Build Three.js Shape/Path from a loop
   */
  buildShapeFromLoop(shape, loop, graph, isOuter) {
    if (loop.edges.length === 0) return;

    let started = false;

    loop.edges.forEach(({ edge, forward }) => {
      if (edge.type === 'circle') {
        // Draw circle
        const numSamples = 32;
        for (let i = 0; i < numSamples; i++) {
          const angle = (2 * Math.PI * i) / numSamples;
          const x = edge.center.x + edge.radius * Math.cos(angle);
          const y = edge.center.y + edge.radius * Math.sin(angle);

          if (!started) {
            shape.moveTo(x, y);
            started = true;
          } else {
            shape.lineTo(x, y);
          }
        }
      } else if (edge.type === 'arc') {
        // Draw arc
        const startPt = forward ? edge.start : edge.end;
        const endPt = forward ? edge.end : edge.start;

        if (!started) {
          shape.moveTo(startPt.x, startPt.y);
          started = true;
        }

        // Use absarc for proper arc rendering
        const startAngle = Math.atan2(
          startPt.y - edge.center.y,
          startPt.x - edge.center.x
        );
        const endAngle = Math.atan2(
          endPt.y - edge.center.y,
          endPt.x - edge.center.x
        );

        shape.absarc(
          edge.center.x,
          edge.center.y,
          edge.radius,
          startAngle,
          endAngle,
          !edge.clockwise // THREE.js uses opposite convention
        );
      } else {
        // Line
        const startPt = forward ? edge.start : edge.end;
        const endPt = forward ? edge.end : edge.start;

        if (!started) {
          shape.moveTo(startPt.x, startPt.y);
          started = true;
        }

        shape.lineTo(endPt.x, endPt.y);
      }
    });
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProfileDetector;
}
